import React, { Fragment, memo } from 'react';
import MUIDataTable from 'mui-datatables';
import { makeStyles } from '@material-ui/core/styles';
import { formatDateTime } from '../../utils/formatters';
import { green, red } from '@material-ui/core/colors';
import { CheckCircle, Clear } from '@material-ui/icons';
import { Grid, Tooltip, TableRow, TableCell } from '@material-ui/core';
import RetryNlsUpdateCell from './RetryNlsUpdateCell';

const useStyles = makeStyles(() => ({
    table: {
        marginTop: '30px',
        width: '85vw',
        marginRight: '5vw',
    },
    detailsContainer: {
        padding: '16px 20px',
        background: '#fafafa',
    },
    detailsLabel: {
        fontWeight: 600,
        marginBottom: '6px',
    },
    detailsPayload: {
        margin: 0,
        padding: '12px',
        background: '#fff',
        border: '1px solid #e0e0e0',
        borderRadius: '4px',
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
        maxHeight: '280px',
        overflowY: 'auto',
        fontFamily: 'monospace',
        fontSize: '12px',
        lineHeight: 1.45,
    },
    detailsSection: {
        marginBottom: '14px',
    },
}));

const columns = [
    {
        name: 'Operation',
        options: {
            filter: true,
            filterOptions: {
                names: ['funding_update', 'payment_update', 'refund_update', 'import_xml'],
            },
        },
    },
    {
        name: 'Status',
        options: {
            filter: true,
            filterOptions: {
                names: ['successful', 'failed'],
            },
            customBodyRender: (value, tableMeta) => {
                const color = value === 'successful' ? green[500] : red[500];
                const IconComponent = value === 'successful' ? CheckCircle : Clear;
                return (
                    <Tooltip title={value.charAt(0).toUpperCase() + value.slice(1)}>
                        <IconComponent style={{ color }} />
                    </Tooltip>
                );
            }
        },
    },
    {
        name: 'User',
        options: { filter: false },
    },
    {
        name: 'HTTP Status',
        options: { filter: false },
    },
    {
        name: 'Timestamp',
        options: { filter: false },
    },
    {
        name: 'Retry',
        options: {
            filter: false,
            sort: false,
            searchable: false,
            download: false,
            viewColumns: false,
            customBodyRender: (log) => <RetryNlsUpdateCell log={log} />,
        },
    },
];

const tablifyLogs = (logs) =>
    logs.map((log) => [
    log.operation,
    log.status,
    log.userEmail || '—',
    log.responseStatus || '—',
    formatDateTime(log.createdAt),
    log,
]);

const prettyPrintPayload = (payload, fallbackText) => {
    if (payload == null || payload === '') return fallbackText;

    if (typeof payload === 'object') {
        try {
            return JSON.stringify(payload, null, 2);
        } catch (_error) {
            return String(payload);
        }
    }

    if (typeof payload !== 'string') return String(payload);

    const trimmedPayload = payload.trim();
    if (!trimmedPayload) return fallbackText;

    try {
        return JSON.stringify(JSON.parse(trimmedPayload), null, 2);
    } catch (_error) {
        return payload;
    }
};

const NlsAuditLogs = ({ dailyProcessingData }) => {
    const classes = useStyles();

    if (!dailyProcessingData) return <h1>Loading...</h1>;

    const tableOptions = {
        filterType: 'dropdown',
        print: false,
        download: false,
        selectableRows: 'none',
        search: true,
        expandableRows: true,
        expandableRowsHeader: false,
        setRowProps: (_row, _dataIndex, rowIndex) =>
            rowIndex % 2 === 0 ? { style: { background: 'snow' } } : {},
        renderExpandableRow: (_rowData, rowMeta) => {
            const log = dailyProcessingData[rowMeta.dataIndex] || {};
            const requestPayload = prettyPrintPayload(log.requestPayload, 'No request payload');
            const responseBody = prettyPrintPayload(log.responseBody, 'No response body');
            const colSpan = columns.length + 1;

            return (
                <TableRow>
                    <TableCell colSpan={colSpan}>
                        <div className={classes.detailsContainer}>
                            <div className={classes.detailsSection}>
                                <div className={classes.detailsLabel}>Request Payload</div>
                                <pre className={classes.detailsPayload}>{requestPayload}</pre>
                            </div>
                            <div>
                                <div className={classes.detailsLabel}>Response Body</div>
                                <pre className={classes.detailsPayload}>{responseBody}</pre>
                            </div>
                        </div>
                    </TableCell>
                </TableRow>
            );
        },
    };

    return (
        <Fragment>
            <Grid container direction="column">
                <Grid item className={classes.table}>
                    <MUIDataTable
                        title="NLS Audit Logs"
                        data={tablifyLogs(dailyProcessingData)}
                        columns={columns}
                        options={tableOptions}
                    />
                </Grid>
            </Grid>
        </Fragment>
    );
};

export default memo(NlsAuditLogs);
